import React, { useMemo } from "react";
import { CalendarClock, Eye, Hourglass, MoonStar, Pause, Radio, Timer } from "lucide-react";
import { Area, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { type ColumnDef } from "@tanstack/react-table";
import { DataTable } from "@/components/ui/data-table";
import { cn } from "@/lib/utils";
import { TradeLogSortableCell } from "../TradeLog/components/tradeLogSortableCell";
import type {
  DeployAccountMetrics,
  DeployLimits,
  DeployNextStep,
  DeploySignal,
  DeployUptime,
  Deployment,
} from "../../../types/strategyDeploy";
import { useNow } from "./Heartbeat";
import { GREEN, RED, SURF, fmtMoney, fmtR, signColor } from "./liveStrategiesFormat";
import {
  liveCardBodyClassName,
  liveCardClassName,
  liveCardTitleClassName,
  liveFigureClassName,
  liveLabelClassName,
  liveOptionTitleClassName,
  liveSmallTextClassName,
} from "./liveStrategiesUi";

// --- what happens next ---------------------------------------------------------------------------

const NEXT_ICON: Record<DeployNextStep["state"], React.ElementType> = {
  idle: Pause,
  listening: Radio,
  market_closed: MoonStar,
  outside_hours: CalendarClock,
  building: Hourglass,
  watching: Eye,
  waiting: Timer,
};

function until(iso: string | null, now: number): string | null {
  if (!iso) return null;
  const s = Math.max(0, Math.round((new Date(iso).getTime() - now) / 1000));
  if (!Number.isFinite(s)) return null;
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

/** What the strategy waits for and when that changes, with a live countdown. */
export function NextStep({ next, compact }: { next?: DeployNextStep | null; compact?: boolean }): JSX.Element | null {
  const now = useNow();
  if (!next) return null;
  const Icon = NEXT_ICON[next.state] ?? Eye;
  const left = until(next.at, now);
  const at = next.at ? new Date(next.at) : null;
  const when = at
    ? at.toLocaleString(undefined, {
        weekday: at.getTime() - now > 20 * 3600_000 ? "short" : undefined,
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;
  const tone = next.state === "idle" ? "var(--muted-foreground)" : SURF;
  if (compact) {
    return (
      <span className={cn(liveSmallTextClassName, "flex min-w-0 items-center gap-1.5")} data-live-next={next.state}>
        <Icon className="size-3 shrink-0" style={{ color: tone }} />
        <span className="truncate">{next.headline}</span>
        {left ? <span className="shrink-0 tabular-nums" style={{ color: tone }}>{left}</span> : null}
      </span>
    );
  }
  return (
    <div className={cn(liveCardClassName, "flex-row items-center gap-4 p-5")} data-live-next={next.state}>
      <span
        className={cn(
          "flex size-9 shrink-0 items-center justify-center rounded-[6px] text-white",
          next.state === "idle" ? "bg-[image:linear-gradient(135deg,#6b7280,#9ca3af)]" : "bg-[image:var(--gradient-primary)]"
        )}
        aria-hidden
      >
        <Icon className="size-4 text-white" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className={liveOptionTitleClassName}>{next.headline}</span>
        <span className={cn(liveSmallTextClassName, "truncate")}>
          {[next.detail, when ? `Next: ${when}` : null].filter(Boolean).join(". ")}
        </span>
      </div>
      {left ? (
        <span className="shrink-0 font-ibm-plex text-lg tabular-nums" style={{ color: tone }} title="Time to the next change">
          {left}
        </span>
      ) : null}
    </div>
  );
}

// --- limits used today ---------------------------------------------------------------------------

function Meter({ label, used, max, text }: { label: string; used: number; max: number | null; text: string }) {
  const pct = max ? Math.min(100, (used / max) * 100) : 0;
  const color = pct >= 100 ? RED : pct >= 66 ? "var(--brand-comparison-period)" : SURF;
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className={liveLabelClassName}>{label}</span>
        <span className={cn(liveFigureClassName, "text-xs")}>{text}</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

/** How much of its daily limits the strategy has used — at a limit it stops for the day. */
export function LimitsMeters({ limits }: { limits?: DeployLimits | null }): JSX.Element | null {
  if (!limits) return null;
  const { trades, open, loss } = limits;
  return (
    <div className={cn(liveCardClassName, liveCardBodyClassName)} data-live-limits="">
      <span className={liveCardTitleClassName}>Today's limits</span>
      <Meter label="Trades" used={trades.used} max={trades.max} text={`${trades.used} / ${trades.max}`} />
      <Meter label="Open now" used={open.used} max={open.max} text={`${open.used} / ${open.max}`} />
      <Meter
        label="Daily loss"
        used={loss.used}
        max={loss.max}
        text={loss.max != null ? `${fmtMoney(-loss.used)} of −$${loss.max.toFixed(0)} (${loss.max_pct}%)` : `${loss.max_pct}%`}
      />
    </div>
  );
}

// --- broker account ------------------------------------------------------------------------------

/** The broker account the strategy trades on, as the broker reports it (refreshed every minute). */
export function AccountStrip({ account, dep }: { account?: DeployAccountMetrics | null; dep: Deployment }): JSX.Element | null {
  if (dep.mode === "paper") return null;
  const cur = account?.currency && account.currency !== "USD" ? ` ${account.currency}` : "";
  const cells: [string, string, string | undefined][] = [
    ["Balance", account?.balance != null ? `${fmtMoney(account.balance, false)}${cur}` : "N/A", undefined],
    ["Equity", account?.equity != null ? `${fmtMoney(account.equity, false)}${cur}` : "N/A", undefined],
    ["Open P&L", account?.unrealized_pnl != null ? fmtMoney(account.unrealized_pnl) : "N/A", signColor(account?.unrealized_pnl)],
    ["Margin used", account?.margin_used != null ? fmtMoney(account.margin_used, false) : "N/A", undefined],
  ];
  return (
    <AccountLine
      title="Broker account"
      cells={cells}
      note={account?.at ? `Updated ${new Date(account.at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}` : "Waiting for the first read…"}
      data-live-account=""
    />
  );
}

/** One line of account figures, no card: it sits above the panels and stays out of the way. */
export function AccountLine({
  title,
  cells,
  note,
  ...rest
}: {
  title: string;
  cells: [string, string, string | undefined][];
  note?: string;
} & React.HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2" {...rest}>
      <span className={liveCardTitleClassName}>{title}</span>
      {cells.map(([k, v, c]) => (
        <span key={k} className="flex min-w-0 items-baseline gap-2">
          <span className={liveLabelClassName}>{k}</span>
          <span className="font-ibm-plex text-sm font-semibold tabular-nums text-gray-500 dark:text-white" style={c ? { color: c } : undefined}>
            {v}
          </span>
        </span>
      ))}
      {note ? <span className={cn(liveSmallTextClassName, "ml-auto")}>{note}</span> : null}
    </div>
  );
}

// --- uptime --------------------------------------------------------------------------------------

const SLOT_COLOR: Record<DeployUptime["slots"][number]["state"], string> = {
  ok: GREEN,
  missed: RED,
  closed: "var(--muted)",
  pending: "color-mix(in oklab, var(--brand-tropical-surf) 35%, transparent)",
  before: "color-mix(in oklab, var(--muted) 45%, transparent)",
  idle: "var(--muted)",
};

const SLOT_WORDS: Record<DeployUptime["slots"][number]["state"], string> = {
  ok: "Checked",
  missed: "Missed",
  closed: "Market closed",
  pending: "Due now",
  before: "Not deployed yet",
  idle: "Not running",
};

/** The last 24 hours of candle checks, slot by slot — proof it has been running. */
export function UptimeStrip({ uptime }: { uptime?: DeployUptime | null }): JSX.Element | null {
  if (!uptime) return null;
  const time = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return (
    <div className={cn(liveCardClassName, liveCardBodyClassName)} data-live-uptime="">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className={liveCardTitleClassName}>Last 24 hours</span>
        <span className={liveSmallTextClassName}>
          {uptime.pct != null ? (
            <>
              <span className="font-ibm-plex font-medium tabular-nums" style={{ color: uptime.missed ? SURF : GREEN }}>{uptime.pct}%</span> of
              candles checked
              {uptime.missed ? (
                <>
                  , <span className="font-ibm-plex font-medium tabular-nums" style={{ color: RED }}>{uptime.missed}</span> missed
                </>
              ) : null}
            </>
          ) : (
            "No trading hours yet"
          )}
        </span>
      </div>
      <div
        className="grid h-6 gap-px overflow-hidden rounded-[3px]"
        style={{ gridTemplateColumns: `repeat(${uptime.slots.length}, minmax(0, 1fr))` }}
        role="img"
        aria-label="Candle checks over the last 24 hours"
      >
        {uptime.slots.map((s) => (
          <span
            key={s.from}
            className="min-w-0"
            style={{ background: SLOT_COLOR[s.state] }}
            title={`${time(s.from)} to ${time(s.to)}: ${SLOT_WORDS[s.state]}`}
          />
        ))}
      </div>
      <div className={cn(liveSmallTextClassName, "flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-[11px]")}>
        <span>{uptime.slots[0] ? time(uptime.slots[0].from) : ""}</span>
        <span className="flex flex-wrap gap-x-3 gap-y-1">
          {(["ok", "missed", "closed"] as const).map((k) => (
            <span key={k} className="flex items-center gap-1">
              <span className="size-2.5 rounded-[2px]" style={{ background: SLOT_COLOR[k] }} />
              {SLOT_WORDS[k]}
            </span>
          ))}
        </span>
        <span>Now</span>
      </div>
    </div>
  );
}

// --- live vs backtest ---------------------------------------------------------------------------

type Expect = NonNullable<NonNullable<Deployment["strategy_snapshot"]["backtest"]>["expect"]> & {
  avg_win_r?: number;
  avg_loss_r?: number;
};

/** Per-trade R mean and spread from the backtest (win rate × average win / loss). */
function tradeDistribution(e: Expect): { mean: number; sd: number } | null {
  const p = e.win_rate;
  const w = e.avg_win_r;
  const l = e.avg_loss_r;
  if (p == null || w == null || l == null) {
    return e.expectancy_r != null ? { mean: e.expectancy_r, sd: 1 } : null;
  }
  const pr = p > 1 ? p / 100 : p;
  const mean = pr * w + (1 - pr) * l;
  const sd = Math.sqrt(pr * (w - mean) ** 2 + (1 - pr) * (l - mean) ** 2);
  return { mean, sd: Number.isFinite(sd) && sd > 0 ? sd : 1 };
}

/** Running R trade by trade against the range the backtest says is normal (±2 spreads). */
/** Live next to the backtest, in the app's data table. */
const COMPARE_COLUMNS: ColumnDef<[string, string, string], unknown>[] = [
  { id: "figure", header: "Figure", enableSorting: false, cell: ({ row }) => <TradeLogSortableCell>{row.original[0]}</TradeLogSortableCell> },
  { id: "live", header: "Live", enableSorting: false, cell: ({ row }) => <TradeLogSortableCell className="font-medium">{row.original[1]}</TradeLogSortableCell> },
  { id: "backtest", header: "Backtest", enableSorting: false, cell: ({ row }) => <TradeLogSortableCell className="font-medium">{row.original[2]}</TradeLogSortableCell> },
];

function perWeek(trades: number, days: number): string {
  const n = (trades / Math.max(days, 1)) * 7;
  return n >= 10 ? String(Math.round(n)) : n.toFixed(1);
}

export function ExpectationCurve({ dep, signals }: { dep: Deployment; signals: DeploySignal[] }): JSX.Element {
  const expect = (dep.strategy_snapshot?.backtest?.expect ?? {}) as Expect;
  const dist = tradeDistribution(expect);
  const closed = useMemo(
    () =>
      signals
        .filter((s) => s.status === "closed" && s.exit_time)
        .sort((a, b) => String(a.exit_time).localeCompare(String(b.exit_time))),
    [signals]
  );
  const horizon = Math.max(20, closed.length + 5);
  const data = useMemo(() => {
    let r = 0;
    return Array.from({ length: horizon + 1 }, (_, n) => {
      if (n > 0 && n <= closed.length) r += closed[n - 1].r_multiple ?? 0;
      const mean = dist ? dist.mean * n : null;
      const band = dist ? 2 * dist.sd * Math.sqrt(n) : null;
      return {
        n,
        live: n <= closed.length ? Number(r.toFixed(2)) : null,
        mean: mean != null ? Number(mean.toFixed(2)) : null,
        band: mean != null && band != null ? [Number((mean - band).toFixed(2)), Number((mean + band).toFixed(2))] : null,
      };
    });
  }, [closed, dist, horizon]);

  const live = closed.reduce((a, s) => a + (s.r_multiple ?? 0), 0);
  const n = closed.length;
  let verdict: { text: string; color?: string };
  if (!dist) verdict = { text: "No backtest to compare against." };
  else if (n < 10) verdict = { text: `Too early to judge: ${n} of 10 trades.` };
  else {
    const lo = dist.mean * n - 2 * dist.sd * Math.sqrt(n);
    const hi = dist.mean * n + 2 * dist.sd * Math.sqrt(n);
    verdict =
      live < lo
        ? { text: "Below what the backtest expects. Worth a look.", color: RED }
        : live > hi
          ? { text: "Above what the backtest expects.", color: GREEN }
          : { text: "Within what the backtest expects.", color: GREEN };
  }
  const color = live >= 0 ? GREEN : RED;
  const lookback = dep.strategy_snapshot?.backtest?.lookback_days ?? null;
  const liveDays = (Date.now() - new Date(dep.created_at).getTime()) / 86_400_000;
  const wins = closed.filter((s) => (s.r_multiple ?? s.pnl ?? 0) > 0).length;
  const pct = (v: number) => `${Math.round(v * 100)}%`;
  // Live next to the backtest it was launched from, figure by figure.
  const compare: [string, string, string][] = [
    ["Trades a week", n ? perWeek(n, liveDays) : "N/A", expect.closed_count && lookback ? perWeek(expect.closed_count, lookback) : "N/A"],
    ["Won", n ? pct(wins / n) : "N/A", expect.win_rate != null ? pct(expect.win_rate) : "N/A"],
    ["Avg R", n ? fmtR(live / n) : "N/A", expect.expectancy_r != null ? fmtR(expect.expectancy_r) : "N/A"],
  ];
  return (
    <div className={cn(liveCardClassName, liveCardBodyClassName)} data-live-expectation="">
      <div className="flex items-center justify-between gap-2">
        <span className={liveCardTitleClassName}>Live vs backtest</span>
        <span className={liveFigureClassName} style={{ color: signColor(live) }}>{fmtR(live)}</span>
      </div>
      <span className={liveSmallTextClassName} style={verdict.color ? { color: verdict.color } : undefined}>{verdict.text}</span>
      <div className="h-[190px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ left: 0, right: 8, top: 6, bottom: 0 }}>
            <XAxis dataKey="n" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} label={{ value: "trades", position: "insideBottomRight", fontSize: 10, offset: 0 }} />
            <YAxis width={38} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={(v: number) => `${v}R`} />
            <ReferenceLine y={0} stroke="var(--border)" />
            <Area dataKey="band" stroke="none" fill={SURF} fillOpacity={0.12} isAnimationActive={false} name="Expected range" />
            <Line dataKey="mean" stroke={SURF} strokeDasharray="4 4" dot={false} strokeWidth={1.25} isAnimationActive={false} name="Backtest average" />
            <Line dataKey="live" stroke={color} dot={false} strokeWidth={2.25} type="stepAfter" connectNulls={false} name="Live" />
            <Tooltip
              formatter={(v, name) => [Array.isArray(v) ? `${fmtR(Number(v[0]))} to ${fmtR(Number(v[1]))}` : fmtR(Number(v)), String(name)]}
              labelFormatter={(l) => `After ${l} trade${Number(l) === 1 ? "" : "s"}`}
              contentStyle={{ background: "var(--popover)", border: "1px solid var(--border)", borderRadius: 6, fontSize: 12 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className={cn(liveSmallTextClassName, "flex flex-wrap gap-4 text-[11px]")}>
        <span className="flex items-center gap-1"><span className="h-0.5 w-3" style={{ background: color }} />Live</span>
        <span className="flex items-center gap-1"><span className="h-0 w-3 border-t border-dashed" style={{ borderColor: SURF }} />Backtest average</span>
        <span className="flex items-center gap-1"><span className="size-2 rounded-[2px]" style={{ background: SURF, opacity: 0.25 }} />Normal range (95%)</span>
      </div>
      <div data-live-compare="">
        <DataTable columns={COMPARE_COLUMNS} data={compare} getRowId={(r) => r[0]} showPagination={false} tableClassName="dark:!bg-sidebar" />
      </div>
    </div>
  );
}
